import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Footer from "../components/Footer";
import HeroSection from "../components/HeroSection";
import PastLabs from "../components/PastLabs";
import QuestionForm from "../components/QuestionForm";
import RegistrationForm from "../components/RegistrationForm";
import SiteHeader from "../components/SiteHeader";
import TrustBar from "../components/TrustBar";
import UpcomingLabs from "../components/UpcomingLabs";
import {
  MAIN_LANDING_HERO_TRUST_LOGOS,
  MAIN_LANDING_TRUST_LOGOS,
} from "../data/associations";
import { getPastLabs, getUpcomingLabs } from "../firebase";

const MOBILE_BREAKPOINT_QUERY = "(max-width: 800px)";

const isMobileViewport = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
};

function HomePage() {
  const location = useLocation();
  const [upcomingLabs, setUpcomingLabs] = useState([]);
  const [pastLabs, setPastLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isActive = true;

    const loadLabs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const [upcomingResponse, pastResponse] = await Promise.all([
          getUpcomingLabs(3),
          getPastLabs(10),
        ]);

        if (isActive) {
          setUpcomingLabs(upcomingResponse);
          setPastLabs(pastResponse);
        }
      } catch (loadError) {
        console.error("Failed to load labs:", loadError);
        if (isActive) {
          setError(
            "We couldn't load lab sessions right now. Please refresh in a moment."
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLabs();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    if (location.hash === "#register") {
      window.requestAnimationFrame(() => {
        const desktopTarget = document.getElementById("home-register-desktop");
        const mobileTarget = document.getElementById("register");

        const target = isMobileViewport()
          ? mobileTarget || desktopTarget
          : desktopTarget || mobileTarget;

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
      return;
    }

    const element = document.querySelector(location.hash);
    if (!element) {
      return;
    }

    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.hash]);

  const nextLab = upcomingLabs[0] || null;
  const registrationSource = {
    sourceType: "public",
    sourceSlug: "main-hub",
    sourceName: "AI Lab Hub",
  };
  const scrollToRegister = useCallback(() => {
    const desktopTarget = document.getElementById("home-register-desktop");
    const mobileTarget = document.getElementById("register");

    const target = isMobileViewport()
      ? mobileTarget || desktopTarget
      : desktopTarget || mobileTarget;

    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <>
      <SiteHeader onRegisterClick={scrollToRegister} />

      <main>
        <HeroSection
          onRegisterClick={scrollToRegister}
          trustLogos={MAIN_LANDING_HERO_TRUST_LOGOS}
          registrationContent={
            <div id="home-register-desktop">
              <h2 className="hero-form-title">Register once for the monthly AI Lab</h2>
              <p className="hero-form-copy">
                It is simple and free. Enter your info once and get the Zoom link,
                calendar invite, and recording every month.
              </p>
              <RegistrationForm
                nextLab={nextLab}
                submitLabel="Get my free invite"
                source={registrationSource}
              />
            </div>
          }
        />

        <section id="register" className="section home-mobile-register-section">
          <div className="container narrow">
            <h2>Register once for the monthly AI Lab</h2>
            <p>
              It's simple and free. 1) Enter your first name, work email, role,
              and state. 2) Click <strong>Get my invite</strong>. 3) We'll email
              you the Zoom link, calendar invite, and recording for each monthly
              60-minute AI Lab for School Leaders. You only need to register once
              and you can opt out with one click at any time.
            </p>

            <div className="register-steps-strip" aria-label="How registration works">
              <article className="register-step-card">
                <span className="register-step-icon">1</span>
                <p>Tell us who you are</p>
              </article>
              <article className="register-step-card">
                <span className="register-step-icon">2</span>
                <p>Get your Zoom link and calendar invite</p>
              </article>
              <article className="register-step-card">
                <span className="register-step-icon">3</span>
                <p>Join live or watch the recording</p>
              </article>
            </div>

            <div className="register-mobile-form">
              <RegistrationForm
                nextLab={nextLab}
                submitLabel="Get my free invite"
                source={registrationSource}
              />
            </div>
          </div>
        </section>

        <TrustBar logos={MAIN_LANDING_TRUST_LOGOS} />

        <UpcomingLabs
          labs={upcomingLabs}
          isLoading={isLoading}
          error={error}
          onRegisterClick={scrollToRegister}
        />

        <PastLabs labs={pastLabs} isLoading={isLoading} error={error} />

        <QuestionForm />
      </main>

      <Footer />
    </>
  );
}

export default HomePage;
